import { InstahomeAmenityType } from "@instacover/helpers"
import type { EntityTable } from "dexie"

import { GeoJsonObj } from "@/components/Map"
import { PredictResponse } from "@/services/model/modelTypes"
import { OrderedStepLocal } from "@/types/stepTypes"
import { InstahomePhotoType } from "@/utils/types"

export const TableNames = {
  session: "session",
  photo: "photo",
  detail: "detail",
  orderedStep: "orderedStep",
  amenity: "amenity",
} as const

export type Session = {
  dexieId: number
  id: string

  createdAt: Date
  updatedAt: Date
  finishedAt: Date | null

  numberOfWalls: number
}

export type Photo = {
  dexieId: number
  id: string

  createdAt: Date
  updatedAt: Date
  uploadedAt?: Date
  uploadFailed?: boolean
  /**
   * -1  not started / awaiting retry
   * 0-100  in progress (only ever written by the Zustand store at runtime)
   * 101  uploaded (set together with `uploadedAt`)
   */
  uploadProgress: number
  deleted?: boolean
  deletedOnServer?: boolean

  instahomeSessionId: string
  wallNumber?: number
  type: InstahomePhotoType
  amenityId?: string

  latitude?: number
  longitude?: number
  accuracy?: number
  // GPS adjusted by BE to be outside of building polygon
  adjustedLatitude?: number
  adjustedLongitude?: number
  // geojson data - for wall photos
  buildingPolygonGeoJson?: GeoJsonObj
  photoCoordinatesGeoJson?: GeoJsonObj
  visibleWallsGeoJson?: GeoJsonObj

  // https://github.com/dexie/Dexie.js/issues/1919
  // Safari has  bug (again) where it doesn't support Blob in IndexedDB
  // so we store img in Uint8Array + mime type and then convert it to Blob on demand
  //blob?: File
  data?: Uint8Array
  mime?: string
  url?: string

  modelResult?: PredictResponse
}

export type Detail = {
  dexieId: number
  id: string

  createdAt: Date
  updatedAt: Date
  uploadedAt?: Date
  uploadFailed?: boolean
  uploadProgress: number
  deleted?: boolean
  deletedOnServer?: boolean

  // Marks a detail to be moved under a new pohoto
  // Used when main photo is retaken, but we want to keep the details
  newInstahomePhotoId?: string

  instahomeSessionId: string
  instahomePhotoId: string

  latitude?: number
  longitude?: number
  accuracy?: number

  // https://github.com/dexie/Dexie.js/issues/1919
  // Safari has  bug (again) where it doesn't support Blob in IndexedDB
  // so we store img in Uint8Array + mime type and then convert it to Blob on demand
  //blob?: File
  data?: Uint8Array
  mime?: string
  url?: string

  modelResult?: PredictResponse
}

export type Amenity<T = InstahomeAmenityType> = {
  dexieId: number
  id: string

  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null

  instahomeSessionId: string
  type: T

  skipped: boolean
}

// Slim types — metadata without binary data (Uint8Array)
export type PhotoSlim = Omit<Photo, "data" | "mime">
export type DetailSlim = Omit<Detail, "data" | "mime">

/* eslint-disable @typescript-eslint/no-unused-vars */
export function stripPhotoBinaryData({ data, mime, ...meta }: Photo): PhotoSlim {
  return meta
}

export function stripDetailBinaryData({ data, mime, ...meta }: Detail): DetailSlim {
  return meta
}
/* eslint-enable @typescript-eslint/no-unused-vars */

export type Tables = {
  [TableNames.session]: EntityTable<Session, "dexieId">
  [TableNames.photo]: EntityTable<Photo, "dexieId">
  [TableNames.detail]: EntityTable<Detail, "dexieId">
  [TableNames.orderedStep]: EntityTable<OrderedStepLocal, "dexieId">
  [TableNames.amenity]: EntityTable<Amenity, "dexieId">
}
